/**
 * FullscreenOverlay Component
 *
 * A fullscreen overlay component supporting three layout variants:
 * - 'standard': Traditional modal (sticky header + body + footer)
 * - 'media': Immersive media (floating controls, no footer)
 *
 * Provides both shorthand and composable APIs following Constellation v10 patterns.
 */

// Import all components first so we can use them in compound object
import { FullscreenOverlay } from './FullscreenOverlay';
import { FullscreenOverlayRoot } from './FullscreenOverlayRoot';
import { FullscreenOverlayTrigger } from './FullscreenOverlayTrigger';
import { FullscreenOverlayPortal } from './FullscreenOverlayPortal';
import { FullscreenOverlayBackdrop } from './FullscreenOverlayBackdrop';
import { FullscreenOverlayContent } from './FullscreenOverlayContent';
import { FullscreenOverlayHeader } from './FullscreenOverlayHeader';
import { FullscreenOverlayBody } from './FullscreenOverlayBody';
import { FullscreenOverlayFooter } from './FullscreenOverlayFooter';
import { FloatingActions } from './FloatingActions';
import { MediaOverlayInfo } from './MediaOverlayInfo';
import { useFullscreenOverlay } from './FullscreenOverlayContext';

// Now export them
export { FullscreenOverlay };
export { FullscreenOverlayRoot };
export { FullscreenOverlayTrigger };
export { FullscreenOverlayPortal };
export { FullscreenOverlayBackdrop };
export { FullscreenOverlayContent };
export { FullscreenOverlayHeader };
export { FullscreenOverlayBody };
export { FullscreenOverlayFooter };
export { FloatingActions };
export { MediaOverlayInfo };
export { useFullscreenOverlay };
export { hasActiveOverlays } from './overlayStack';

// Export types
export type {
  FullscreenOverlayProps,
  FullscreenOverlayRootProps,
  FullscreenOverlayTriggerProps,
  FullscreenOverlayPortalProps,
  FullscreenOverlayBackdropProps,
  FullscreenOverlayContentProps,
  FullscreenOverlayHeaderProps,
  FullscreenOverlayBodyProps,
  FullscreenOverlayFooterProps,
  FloatingActionsProps,
  MediaOverlayInfoProps,
  OverlayVariant,
  ContentPadding,
  FooterVariant,
  HeaderLayout,
  ResponsiveVariant,
} from './types';

// Composable compound component (Constellation v10 pattern)
// Now we can use the imported names in the compound object
const FullscreenOverlayCompound = Object.assign(FullscreenOverlay, {
  Root: FullscreenOverlayRoot,
  Trigger: FullscreenOverlayTrigger,
  Portal: FullscreenOverlayPortal,
  Backdrop: FullscreenOverlayBackdrop,
  Content: FullscreenOverlayContent,
  Header: FullscreenOverlayHeader,
  Body: FullscreenOverlayBody,
  Footer: FullscreenOverlayFooter,
  FloatingActions: FloatingActions,
  MediaOverlayInfo: MediaOverlayInfo,
});

export default FullscreenOverlayCompound;
